import { useEffect, useState } from 'react'
import { client } from '../lib/client'
import type { ServerDetail, AccountDetail } from '../types'

export type Selection =
  | { kind: 'server'; id: string }
  | { kind: 'account'; id: string }
  | null

interface State {
  server: ServerDetail | null
  account: AccountDetail | null
  loading: boolean
  error: string | null
}

/** Fetches the appropriate drilldown whenever the selection changes. */
export function useDetail(selection: Selection) {
  const [state, setState] = useState<State>({
    server: null,
    account: null,
    loading: false,
    error: null,
  })

  useEffect(() => {
    if (!selection) {
      setState({ server: null, account: null, loading: false, error: null })
      return
    }

    let cancelled = false
    setState({ server: null, account: null, loading: true, error: null })

    const run = async () => {
      try {
        if (selection.kind === 'server') {
          const server = await client.server(selection.id)
          if (!cancelled)
            setState({ server, account: null, loading: false, error: null })
        } else {
          const account = await client.account(selection.id)
          if (!cancelled)
            setState({ server: null, account, loading: false, error: null })
        }
      } catch (err) {
        if (!cancelled)
          setState({
            server: null,
            account: null,
            loading: false,
            error: err instanceof Error ? err.message : 'Failed to load detail',
          })
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [selection?.kind, selection?.id])

  return state
}
